/** Catalog upserts from sync-pull JSON (no node:fs — safe in browser). */

function normalizePublicId(value) {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

export function pickPublicId(r) {
  return normalizePublicId(r.publicId ?? r.public_id);
}

export function readNumberOrNull(r, k) {
  const v = r[k];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const n = Number(typeof v === 'string' ? v.trim() : '');
  return Number.isFinite(n) ? n : null;
}

export function asObjectArray(v) {
  return Array.isArray(v) ? v.filter((x) => x && typeof x === 'object') : [];
}

const str = (r, k, alt = k, trim = true) => {
  const v = r[k] ?? r[alt];
  return typeof v === 'string' ? (trim ? v.trim() : v) : '';
};

/** Sync tombstone: `estado` 0 / false / "0" marks row inactive locally. */
export function readEstadoActivo(r) {
  const v = r.estado;
  if (v === 0 || v === false || v === '0') return false;
  return true;
}

function row(r, fields) {
  const id = pickPublicId(r);
  if (!id) return null;
  const out = [id];
  for (const f of fields) {
    const { k, alt = k, trim = true, num = false, req = true, def } = typeof f === 'string' ? { k: f } : f;
    const v = num ? readNumberOrNull(r, k) : str(r, k, alt, trim);
    if (req && (v === '' || v == null)) return null;
    out.push(num ? (v ?? def ?? 0) : v);
  }
  return out;
}

function upsert(db, table, cols, upd, rows, map) {
  if (!rows.length) return 0;
  const insertSql = `INSERT INTO ${table} (${[...cols, 'estado', 'fecha_actualizacion'].join(', ')})
    VALUES (${[...cols.map(() => '?'), '1', "datetime('now')"].join(', ')})
    ON CONFLICT(public_id) DO UPDATE SET ${[...upd.map((c) => `${c} = excluded.${c}`), 'estado = 1', "fecha_actualizacion = datetime('now')"].join(', ')}`;
  const tombstoneSql = `UPDATE ${table} SET estado = 0, fecha_actualizacion = datetime('now') WHERE public_id = ?`;
  let n = 0;
  for (const r of rows) {
    const id = pickPublicId(r);
    if (!id) continue;
    if (!readEstadoActivo(r)) {
      db.run(tombstoneSql, [id]);
      n++;
      continue;
    }
    const p = map(r);
    if (!p) continue;
    db.run(insertSql, p);
    n++;
  }
  return n;
}

const NOMBRE = ['public_id', 'nombre'];
const NOMBRE_UPD = ['nombre'];

const CATALOGS = [
  { key: 'comunas', table: 'comunas', cols: NOMBRE, upd: NOMBRE_UPD, fields: ['nombre'] },
  { key: 'nutricionistas', table: 'nutricionistas', cols: NOMBRE, upd: NOMBRE_UPD, fields: ['nombre'] },
  { key: 'productosReposicion', table: 'productos_reposicion', cols: NOMBRE, upd: NOMBRE_UPD, fields: ['nombre'] },
  { key: 'mediosReporte', table: 'medios_reporte', cols: NOMBRE, upd: NOMBRE_UPD, fields: ['nombre'] },
  { key: 'motivosReposicion', table: 'motivos_reposicion', cols: NOMBRE, upd: NOMBRE_UPD, fields: ['nombre'] },
  { key: 'parentescos', table: 'parentescos', cols: NOMBRE, upd: NOMBRE_UPD, fields: ['nombre'] },
  { key: 'tiposDocumento', table: 'tipos_documento', cols: NOMBRE, upd: NOMBRE_UPD, fields: ['nombre'] },
  {
    key: 'puntosEntrega',
    table: 'puntos_entrega',
    cols: ['public_id', 'comuna_public_id', 'nombre', 'direccion', 'lat', 'lng'],
    upd: ['comuna_public_id', 'nombre', 'direccion', 'lat', 'lng'],
    fields: [{ k: 'comunaPublicId', alt: 'comuna_public_id' }, 'nombre', { k: 'direccion', trim: false, req: false }, { k: 'lat', num: true, req: false }, { k: 'lng', num: true, req: false }],
  },
  { key: 'itemsEntrega', table: 'items_entrega', cols: NOMBRE, upd: NOMBRE_UPD, fields: ['nombre'] },
  {
    key: 'beneficiarios',
    table: 'beneficiarios',
    cols: ['public_id', 'documento', 'nombre', 'tipo_documento_public_id', 'telefono1', 'telefono2', 'whatsapp'],
    upd: ['documento', 'nombre', 'tipo_documento_public_id', 'telefono1', 'telefono2', 'whatsapp'],
    fields: ['documento', 'nombre', { k: 'tipoDocumentoPublicId', alt: 'tipo_documento_public_id', req: false }, { k: 'telefono1', req: false, def: '' }, { k: 'telefono2', req: false, def: '' }, { k: 'whatsapp', req: false, def: '' }],
  },
  {
    key: 'ciclos',
    table: 'ciclos',
    cols: ['public_id', 'nombre', 'numero', 'fecha_inicio', 'fecha_fin'],
    upd: ['nombre', 'numero', 'fecha_inicio', 'fecha_fin'],
    fields: ['nombre', { k: 'numero', num: true, req: false, def: 0 }, { k: 'fecha_inicio', alt: 'fechaInicio', req: false, def: '' }, { k: 'fecha_fin', alt: 'fechaFin', req: false, def: '' }],
  },
  {
    key: 'productosEntrega',
    table: 'productos_entrega',
    cols: ['public_id', 'nombre', 'codigo'],
    upd: ['nombre', 'codigo'],
    fields: ['nombre', { k: 'codigo', trim: false, req: false }],
  },
];

export const SYNC_PULL_REFERENCE_KEYS = ['usuarios', ...CATALOGS.map((c) => c.key)];

const USUARIOS = {
  table: 'usuarios',
  cols: ['public_id', 'nombre', 'nombre_usuario', 'email', 'password_hash'],
  upd: ['nombre', 'nombre_usuario', 'email', 'password_hash'],
  fields: ['nombre', { k: 'nombreUsuario', alt: 'nombre_usuario' }, { k: 'email', req: false, def: '' }, { k: 'passwordHash', alt: 'password_hash' }],
};

const ITEM_LINKS = [
  { section: 'puntosEntrega', table: 'punto_entrega_item', entityCol: 'punto_entrega_public_id', itemCol: 'item_entrega_public_id', idsKey: 'itemsEntregaPublicIds', idsAlt: 'items_entrega_public_ids' },
  { section: 'nutricionistas', table: 'nutricionista_item', entityCol: 'nutricionista_public_id', itemCol: 'item_entrega_public_id', idsKey: 'itemsPublicIds', idsAlt: 'items_public_ids' },
  { section: 'productosEntrega', table: 'producto_entrega_item', entityCol: 'producto_entrega_public_id', itemCol: 'item_entrega_public_id', idsKey: 'itemsPublicIds', idsAlt: 'items_public_ids' },
  { section: 'productosReposicion', table: 'producto_reposicion_item', entityCol: 'producto_reposicion_public_id', itemCol: 'item_entrega_public_id', idsKey: 'itemsPublicIds', idsAlt: 'items_public_ids' },
  { section: 'beneficiarios', table: 'beneficiario_item', entityCol: 'beneficiario_public_id', itemCol: 'item_entrega_public_id', idsKey: 'itemsPublicIds', idsAlt: 'items_public_ids' },
];

export function backfillItemsEntregaUnidadPrograma(db, defaultUnidad) {
  const value = typeof defaultUnidad === 'string' ? defaultUnidad.trim() : '';
  if (!value) return 0;
  db.run(
    `UPDATE items_entrega SET unidad_programa = ?, fecha_actualizacion = datetime('now')
     WHERE estado = 1 AND trim(COALESCE(unidad_programa, '')) = ''`,
    [value],
  );
  return typeof db.getRowsModified === 'function' ? db.getRowsModified() : 0;
}

/** Maps API rolId (often numeric) to local `roles.public_id`. */
export function resolveUsuarioRolPublicId(rolId) {
  const id = String(rolId ?? '').trim();
  if (!id) return '';
  if (id === '1') return 'rol_admin';
  return id;
}

function readStringIdArray(r, camelKey, snakeKey) {
  if (!(camelKey in r) && !(snakeKey in r)) return null;
  const v = r[camelKey] ?? r[snakeKey];
  if (!Array.isArray(v)) return [];
  return v.map((x) => normalizePublicId(x)).filter(Boolean);
}

function replaceJunctionRows(db, table, entityCol, itemCol, entityPublicId, itemPublicIds) {
  db.run(`DELETE FROM ${table} WHERE ${entityCol} = ?`, [entityPublicId]);
  for (const itemPublicId of itemPublicIds) {
    db.run(`INSERT INTO ${table} (${entityCol}, ${itemCol}) VALUES (?, ?)`, [entityPublicId, itemPublicId]);
  }
}

function mergeItemLinks(db, rows, table, entityCol, itemCol, idsKey, idsAlt) {
  let n = 0;
  for (const r of rows) {
    const entityId = pickPublicId(r);
    const itemIds = readStringIdArray(r, idsKey, idsAlt);
    if (!entityId || itemIds === null || !readEstadoActivo(r)) continue;
    replaceJunctionRows(db, table, entityCol, itemCol, entityId, itemIds);
    n += itemIds.length;
  }
  return n;
}

function mergeItemEntregaCiclos(db, rows) {
  let n = 0;
  for (const r of rows) {
    const linkedItemId = typeof r.itemEntregaPublicId === 'string' ? r.itemEntregaPublicId.trim() : '';
    const cicloId = pickPublicId(r);
    if (!cicloId || !linkedItemId || !readEstadoActivo(r)) continue;
    db.run(`DELETE FROM item_entrega_ciclo WHERE ciclo_public_id = ?`, [cicloId]);
    db.run(`INSERT INTO item_entrega_ciclo (item_entrega_public_id, ciclo_public_id) VALUES (?, ?)`, [linkedItemId, cicloId]);
    n++;
  }
  return n;
}

function readRolId(r) {
  const v = r.rolId ?? r.rol_id;
  if (typeof v === 'number' && Number.isFinite(v)) return resolveUsuarioRolPublicId(String(v));
  if (typeof v === 'string') return resolveUsuarioRolPublicId(v.trim());
  return '';
}

function mergeUsuarioRol(db, rows) {
  let n = 0;
  for (const r of rows) {
    const usuarioId = pickPublicId(r);
    const rolId = readRolId(r);
    if (!usuarioId || !rolId || !readEstadoActivo(r)) continue;
    db.run(`DELETE FROM usuario_rol WHERE usuario_id = (SELECT id FROM usuarios WHERE public_id = ?)`, [usuarioId]);
    db.run(
      `INSERT INTO usuario_rol (usuario_id, rol_id)
       SELECT u.id, r.id FROM usuarios u, roles r
       WHERE u.public_id = ? AND r.public_id = ?`,
      [usuarioId, rolId],
    );
    n++;
  }
  return n;
}

function countReferenceSectionRows(sections) {
  const defaultUnidad = typeof sections.unidadPrograma === 'string' ? sections.unidadPrograma.trim() : '';
  const catalogs = CATALOGS.filter((c) => sections[c.key] !== undefined);
  let rowCount = sections.usuarios !== undefined ? asObjectArray(sections.usuarios).length : 0;
  for (const c of catalogs) rowCount += asObjectArray(sections[c.key]).length;
  return { rowCount, defaultUnidad, catalogs };
}

function applyReferenceCatalogSections(db, sections) {
  const { rowCount, defaultUnidad, catalogs } = countReferenceSectionRows(sections);
  if (rowCount === 0 && !defaultUnidad) return 0;

  let n = 0;
  if (sections.usuarios !== undefined) {
    const rows = asObjectArray(sections.usuarios);
    n += upsert(db, USUARIOS.table, USUARIOS.cols, USUARIOS.upd, rows, (r) => row(r, USUARIOS.fields));
    n += mergeUsuarioRol(db, rows);
  }
  for (const c of catalogs) {
    n += upsert(db, c.table, c.cols, c.upd, asObjectArray(sections[c.key]), (r) => row(r, c.fields));
  }
  for (const link of ITEM_LINKS) {
    if (sections[link.section] === undefined) continue;
    n += mergeItemLinks(
      db,
      asObjectArray(sections[link.section]),
      link.table,
      link.entityCol,
      link.itemCol,
      link.idsKey,
      link.idsAlt,
    );
  }
  if (sections.ciclos !== undefined) n += mergeItemEntregaCiclos(db, asObjectArray(sections.ciclos));
  if (defaultUnidad) n += backfillItemsEntregaUnidadPrograma(db, defaultUnidad);
  return n;
}

const REFERENCE_JUNCTION_TABLES = [...new Set([...ITEM_LINKS.map((l) => l.table), 'item_entrega_ciclo'])];
const REFERENCE_CATALOG_TABLES = CATALOGS.map((c) => c.table);

function deleteAllReferenceRows(db, table) {
  const exists = db.exec(`SELECT 1 FROM sqlite_master WHERE type='table' AND name='${table}' LIMIT 1`);
  if (exists.length === 0 || exists[0].values.length === 0) return;
  db.run(`DELETE FROM ${table}`);
}

/** Deletes pull-managed reference rows; keeps entregas/reposiciones and RBAC seeds. */
export function wipeReferenceCatalogSections(db) {
  for (const table of REFERENCE_JUNCTION_TABLES) deleteAllReferenceRows(db, table);
  deleteAllReferenceRows(db, 'usuario_rol');
  deleteAllReferenceRows(db, 'usuarios');
  for (const table of REFERENCE_CATALOG_TABLES) deleteAllReferenceRows(db, table);
}

/** Wipe local reference catalogs then import pull payload (transactional). */
export function repopulateReferenceCatalogSections(db, sections) {
  const { rowCount, defaultUnidad } = countReferenceSectionRows(sections);
  if (rowCount === 0 && !defaultUnidad) {
    throw new Error('No hay catálogos para repoblar la base de datos local.');
  }

  // FK enforcement OFF for the bulk reload: wiping parents (comunas, tipos_documento)
  // before children, and catalog rows referenced by pending entregas/reposiciones (RESTRICT),
  // would otherwise throw "FOREIGN KEY constraint failed". Toggle must be outside the txn.
  // ponytail: server payload is the source of truth; re-insert restores integrity for known rows.
  db.run('PRAGMA foreign_keys = OFF');
  db.run('BEGIN IMMEDIATE');
  try {
    wipeReferenceCatalogSections(db);
    const n = applyReferenceCatalogSections(db, sections);
    db.run('COMMIT');
    return n;
  } catch (e) {
    db.run('ROLLBACK');
    throw e;
  } finally {
    db.run('PRAGMA foreign_keys = ON');
  }
}

export function mergeReferenceCatalogSections(db, sections) {
  db.run('BEGIN IMMEDIATE');
  try {
    const n = applyReferenceCatalogSections(db, sections);
    db.run('COMMIT');
    return n;
  } catch (e) {
    db.run('ROLLBACK');
    throw e;
  }
}
